import { useState } from 'react'
import { Logger, Analytics } from '../../../dbcom'
import { DeseretBook } from '../../../dbcom/api'

const logger = new Logger({ context: 'product/reviews/useFeedback' })

/**
 * Hook for providing feedback for product reviews. Currently setup to
 * add ratings without comments.
 * @param {array} feedbackReviews - All feedback reviews belonging to a product review
 * @param {number} reviewId - The unique id for the review containing the feedback review
 * @returns {object} object containing feedbackCount, provideFeedback and userFeedback.
 */
export const useFeedback = (feedbackReviews, reviewId) => {
  const userId = Number(window.__USER__.id)

  /**
   * Finds a feedback review matching a userId
   * @param {array} feedbackArr - Array of feedback reviews
   *
   * @returns {object} the feedback review object matching the userId
   */
  const getUserFeedback = feedbackArr =>
    feedbackArr.filter(feedback => feedback.userId === userId)[0]

  // initial state looks for current user feedback
  const [userFeedback, setUserFeedback] = useState(
    getUserFeedback(feedbackReviews),
  )

  /**
   * Sometimes we can get out of sync with the current feedback.
   * It may have been removed from another instance of the review.
   * (the most helpful section) If this is the case then we will
   * retrieve the up to date feedback review array from the current
   * review and re set the userFeedback state
   */
  const getReviewFeedback = async () => {
    try {
      const response = await DeseretBook.review({ id: reviewId }).get()

      if (response.error) {
        throw new Error(response.error)
      }

      if (response && response.feedbackReviews) {
        setUserFeedback(getUserFeedback(response.feedbackReviews))
      }
    } catch (error) {
      logger.error(`Unable to get review. Message: ${error}`)
    }
  }

  /**
   * Create feedback for a review
   * ex: Liking/Disliking a review for the first time.
   * @param {number} rating - 1 for negative, 5 for positive
   */
  const addFeedback = async rating => {
    try {
      const response = await DeseretBook.review({ id: reviewId })
        .feedback()
        .create({
          rating: String(rating),
          userId,
        })

      if (response.error) {
        // We have out of date information if we can't create new feedback
        // We will get the most recent feedback list and update userFeedback
        getReviewFeedback()
        throw new Error(response.error)
      }

      setUserFeedback(response)
    } catch (error) {
      logger.error(`Unable to add user feedback. Message: ${error}`)
    }
  }

  /**
   * Updates feedback
   * ex: Changing a like to dislike
   * @param {number} rating - 1 for negative, 5 for positive
   */
  const updateFeedback = async rating => {
    try {
      const response = await DeseretBook.review({ id: reviewId })
        .feedback({ id: userFeedback.id })
        .update({
          rating: String(rating),
          userId,
        })

      if (response.error) {
        // The feedback may have been removed. Try to create one instead
        addFeedback(rating)
        throw new Error(response.error)
      }

      setUserFeedback(response)
    } catch (error) {
      logger.error(`Unable to update user feedback. Message: ${error}`)
    }
  }

  /**
   * Removes the users feedback
   */
  const removeFeedback = async () => {
    try {
      await DeseretBook.review({ id: reviewId })
        .feedback({ id: userFeedback.id })
        .delete({ userId })

      setUserFeedback(undefined)
    } catch (error) {
      logger.error(`Unable to remove user feedback. Message: ${error}`)
    }
  }

  /**
   * Decides whether we should create/update/destroy review feedback
   * @param {number} rating - Can either be 1 or 5
   * 1 - User does not agree with a users review
   * 5 - User agrees with a users review
   */
  const provideFeedback = rating => {
    Analytics.event(
      {
        category: 'Product Review',
        action: `User clicked the ${
          rating === 5 ? 'helpful' : 'not helpful'
        } feedback button`,
      },
      { isInteractive: true },
    )

    // We can't do anything without the user ID
    if (!userId) {
      return
    }

    if (!userFeedback) {
      addFeedback(rating)
    } else if (userFeedback && userFeedback.rating !== rating) {
      updateFeedback(rating)
    } else if (userFeedback && userFeedback.rating === rating) {
      removeFeedback()
    }
  }

  // Removes user feedback from array of feedback reviews
  const filteredFeedback = feedbackReviews.filter(
    feedback =>
      userId !== feedback.userId &&
      (!userFeedback || userFeedback.id !== feedback.id),
  )

  const positiveFeedback = filteredFeedback.filter(
    feedback => feedback.rating === 5,
  )

  const negativeFeedback = filteredFeedback.filter(
    feedback => feedback.rating === 1,
  )

  if (userFeedback && userFeedback.rating === 5) {
    positiveFeedback.push(userFeedback)
  } else if (userFeedback) {
    negativeFeedback.push(userFeedback)
  }

  return {
    provideFeedback,
    userFeedback,
    positiveFeedback: positiveFeedback.length,
    negativeFeedback: negativeFeedback.length,
  }
}
